/* eslint-disable react/sort-comp,import/prefer-default-export */
/* @flow */

import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import type { Connector } from 'react-redux';
import './../../../../styles_common/questionnaire_form.scss';
import * as notify from '../../../../../../utils/notify';
import {
  QuestionnaireExperience as QuestionnaireExperienceContext,
  Reducer,
  Dispatch,
} from '../../../../../../types/index';
import TextInput from '../../../../../../components/TextInput/index.uncontrolled';
import Button from '../../../../../../components/Button/index';
import Title from '../../../../../../components/Title/index';
import ConfirmCheckbox from '../../../../../../components/ConfirmCheckbox/index';
import MultiSelect from '../../../../../../components/MultiSelect/index';
import { QuestionnaireIndex } from '../QuestionnaireIndex/index.v1';
import Paragraph from '../../../../../../components/Paragraph/index';
import FundTool from '../../../../../../components/FundTool/index';
import DateTimeZone from '../../../../../../utils/date-time-zone';
import { saveSingleGeneralInfoField } from '../../../../util';

type Props = {
  questionnaireContext: QuestionnaireExperienceContext,
};

export class QuestionnaireForm extends PureComponent {
  props: Props;
  exhibitDInputRefs = {};
  fullLegalNameRef = null;

  componentDidMount() {
    this.updateTextAnswersState();
    notify.hideNotification();
  }

  updateTextAnswersState = () => {
    const { questionnaireContext } = this.props;
    if (this.fullLegalNameRef) {
      this.fullLegalNameRef.value =
        (questionnaireContext.investorFund.generalInfo || {}).name || '';
    }
    Object.keys(this.exhibitDInputRefs).forEach((id) => {
      const question = this.parseQuestionDataFromId(id);
      this.exhibitDInputRefs[id].value = question ? question.answer : '';
    });
  };

  handlerFormSubmit = async (event) => {
    event.preventDefault();
    const pendingUpdates = Object.keys(this.exhibitDInputRefs).map((id) => {
      const input = this.exhibitDInputRefs[id];
      const current = this.parseQuestionDataFromId(id);
      return !current || current.answer !== input.value
        ? this.props.updateMultiSelectFormAnswer(id, input.value)
        : null;
    }).filter(Boolean);
    await Promise.all(pendingUpdates);
    this.props.handleRedirect({ preventDefault: () => {} }, 1);
  };

  parseCheckboxStateFromId = (identifier) => {
    const question = this.parseQuestionDataFromId(identifier);
    return question ? [question.answer === 'Yes', question.answer === 'No'] : [false, false];
  };

  parseMultiSelectStateFromId = (identifier) => {
    const question = this.parseQuestionDataFromId(identifier);
    return Boolean(question && question.answer === 'Yes');
  };

  parseQuestionDataFromId = (identifier) => {
    const { questionnaireContext } = this.props;
    const questions = questionnaireContext.investorFund.questionnaire.questions || [];
    return questions.find((question) => question.descriptionIdentifier === identifier);
  };

  isQuestionDisabled = (identifier) => {
    const { questionnaireContext } = this.props;
    const metadata = questionnaireContext.multiSelectFormMetadata || {};
    return (metadata.disabled || []).includes(identifier);
  };

  handlerConfirm = (event) => {
    event.preventDefault();
    const answer = event.target.nextSibling.innerHTML;
    const descriptionIdentifier = event.target.closest('div').parentElement.parentElement.id;
    this.props.updateMultiSelectFormAnswer(descriptionIdentifier, answer);
  };

  handleTextInputBlur = (event) => {
    event.preventDefault();
    const current = this.parseQuestionDataFromId(event.target.id);
    if (!current || current.answer !== event.target.value) {
      this.props.updateMultiSelectFormAnswer(event.target.id, event.target.value);
    }
  };

  handleFullLegalNameBlur = (event) => {
    event.preventDefault();
    const { questionnaireContext } = this.props;
    const currentName = (questionnaireContext.investorFund.generalInfo || {}).name || '';
    if (currentName !== event.target.value) {
      saveSingleGeneralInfoField(
        { name: 'name', value: event.target.value },
        this.props
      );
    }
  };

  setExhibitDInputRef = (id, node) => {
    if (node) this.exhibitDInputRefs[id] = node;
  };

  renderExhibitDInput = (item) => (
    <TextInput
      inputData={{
        id: item.id,
        displayName: item.displayName,
        isRequired: false,
        size: 'large',
        hasError: false,
        type: item.type || 'text',
        disabled: false,
        showHelp: Boolean(item.helpText),
        helpText: item.helpText || '',
      }}
      reference={(node) => this.setExhibitDInputRef(item.id, node)}
      onBlurHandler={this.handleTextInputBlur}
    />
  );

  renderFullLegalNameInput = () => (
    <TextInput
      inputData={{
        id: 'aml_full_legal_name',
        displayName: 'Full Legal Name of Investor',
        isRequired: false,
        size: 'large',
        hasError: false,
        type: 'text',
      }}
      reference={(node) => { this.fullLegalNameRef = node; }}
      onBlurHandler={this.handleFullLegalNameBlur}
    />
  );

  renderConfirm = (item) => (
    <ConfirmCheckbox
      data={{
        id: [`${item.id}_yes`, `${item.id}_no`, item.id],
        title: item.displayName,
        isSelected: this.parseCheckboxStateFromId(item.id),
        name: item.id,
        size: 'large',
        showHelp: Boolean(item.helpText),
        helpText: item.helpText || '',
        footnoteMarker: item.footnoteMarker,
        isDisabled: [this.isQuestionDisabled(item.id), this.isQuestionDisabled(item.id)],
      }}
      onChangeHandler={this.handlerConfirm}
    />
  );

  renderBeneficialOwnershipConfirm = () => (
    <ConfirmCheckbox
      data={{
        id: [
          'aml_beneficial_ownership_yes',
          'aml_beneficial_ownership_no',
          'aml_beneficial_ownership',
        ],
        title: '1. Does any natural person, directly or indirectly, through any contract, arrangement, understanding, relationship or otherwise, own 25% or more of the equity interests in the undersigned?',
        isSelected: this.parseCheckboxStateFromId('aml_beneficial_ownership'),
        name: 'aml_beneficial_ownership',
        size: 'large',
        showHelp: false,
        helpText: '',
        isDisabled: [
          this.isQuestionDisabled('aml_beneficial_ownership'),
          this.isQuestionDisabled('aml_beneficial_ownership'),
        ],
      }}
      onChangeHandler={this.handlerBeneficialOwnershipConfirm}
    />
  );

  renderGeneralQuestion = (item) => (
    <React.Fragment key={item.id}>
      {this.renderConfirm(item)}
      {item.id === 'aml_general_increased_monitoring' &&
        this.parseMultiSelectStateFromId(item.id) &&
        <div className='row'>
          {this.renderExhibitDInput({
            id: 'aml_general_increased_monitoring_jurisdiction',
            displayName: 'If "Yes", please list the jurisdiction:',
          })}
        </div>}
      {item.id === 'aml_general_funds_through_restricted_bank' &&
        this.parseMultiSelectStateFromId(item.id) &&
        <div className='row'>
          {this.renderExhibitDInput({
            id: 'aml_general_funds_through_restricted_bank_details',
            displayName: 'If "Yes", please provide details, including the name and jurisdiction of the relevant bank and the applicable category described above:',
          })}
        </div>}
    </React.Fragment>
  );

  handlerExhibitDCheckbox = (event) => {
    this.props.updateMultiSelectFormAnswer(event.target.id, event.target.checked ? 'Yes' : 'No');
  };

  handlerPartBSelection = (event) => {
    const answer = event.target.checked ? 'Yes' : 'No';
    const { id } = event.target;
    this.props.updateMultiSelectFormAnswer(id, answer);
  };

  handlerBeneficialOwnershipConfirm = (event) => {
    event.preventDefault();
    const answer = event.target.nextSibling.innerHTML;
    this.props.updateMultiSelectFormAnswer('aml_beneficial_ownership', answer);
    ['aml_beneficial_ownership_pooled_vehicle', 'aml_beneficial_ownership_nonprofit'].forEach((id) => {
      this.props.updateMultiSelectFormAnswer(id, 'No');
    });
  };

  handlerBeneficialOwnershipExemption = (event, options) => {
    const answer = event.target.checked ? 'Yes' : 'No';
    this.props.updateMultiSelectFormAnswer(event.target.id, answer);
    if (answer === 'Yes') {
      options.filter((item) => item.id !== event.target.id).forEach((item) => {
        this.props.updateMultiSelectFormAnswer(item.id, 'No');
      });
      this.props.updateMultiSelectFormAnswer('aml_beneficial_ownership', 'N/A');
    }
  };

  exhibitDCheckboxes = (items, disabled = false) => items.map((item) => {
    const id = item.id || item[0];
    const displayName = item.displayName || item[1];
    const periodIndex = id.indexOf('aml_part_e_') === 0 ? displayName.indexOf('.') : -1;
    const underlinedDisplayName = periodIndex > -1
      ? `[[${displayName.slice(0, periodIndex + 1)}<]]${displayName.slice(periodIndex + 1)}`
      : displayName;
    return {
      id,
      displayName: underlinedDisplayName,
      name: id,
      identifier: id,
      isSelected: this.parseMultiSelectStateFromId(id),
      helpText: item.helpText || item[4] || '',
      showHelp: Boolean(item.helpText || item[4]),
      footnoteMarker: item.footnoteMarker || item[3],
      disabled: disabled || this.isQuestionDisabled(id),
    };
  });

  renderPartERequirement = (categoryId, requirement) => {
    const italicPhrases = {
      aml_part_e_natural_person: requirement.startsWith('(b)')
        ? '(only if not listed on photo identification)'
        : requirement.startsWith('For jointly held accounts')
          ? requirement
          : '',
      aml_part_e_partnership_llc: requirement.startsWith('(a)')
        ? '(or equivalent constitutional documents)'
        : '',
      aml_part_e_foundation_endowment: requirement.startsWith('(a)')
        ? '(or equivalent)'
        : '',
    };
    const italicPhrase = italicPhrases[categoryId] || '';
    if (!italicPhrase) return <Paragraph key={requirement} text={requirement} />;
    const phraseIndex = requirement.indexOf(italicPhrase);
    return (
      <Paragraph key={requirement}>
        <p>{requirement.slice(0, phraseIndex)}<i>{italicPhrase}</i>{requirement.slice(phraseIndex + italicPhrase.length)}</p>
      </Paragraph>
    );
  };

  renderExhibitDCheckboxes = (items, handler, disabled = false) => (
    <MultiSelect
      data={{ id: `aml-${items[0].id || items[0][0]}`, size: 'large' }}
      content={this.exhibitDCheckboxes(items, disabled)}
      onChangeHandler={handler}
    />
  );

  render() {
    const { questionnaireContext } = this.props;
    const questionnaire = questionnaireContext.investorFund.questionnaire;
    const exhibitDAml = questionnaireContext.currentVersionQuestions.exhibitDAml;
    const readableDate = questionnaire && questionnaire.updatedAt ? new DateTimeZone(questionnaire.updatedAt) : '';
    const formattedDate = readableDate !== '' ? readableDate.getLocalDate() : readableDate;
    const partBExemptionSelected = exhibitDAml.partBCategories.some((item) =>
      item[0] !== 'aml_part_b_foreign_governmental_entity' &&
      this.parseMultiSelectStateFromId(item[0])
    );

    return questionnaire.questions ? (
      <div className='margin-bottom-spacing'>
        <FundTool
          data={{
            saveLaterAble: true,
            modalToggle: 'modal',
            modalTarget: 'saveLaterPopup',
            lastSavedTimeStamp: formattedDate,
          }}
          printHandler={null}
          saveLaterHandler={null}
        />
        <div className='row'>
          <div className='col-lg-12 col-md-12 col-sm-12 col-xs-12'>
            <div className='content-bg main-content-area clearfix'>
              <div className='row'>
                <QuestionnaireIndex />
                <div className='col-lg-8 col-md-8 col-sm-8 col-xs-12'>
                  <div className='main'>
              <h4 className='color-dark-green align-header-text-sub-header questionnaire-header'>Questionnaire</h4>
              <form onSubmit={this.handlerFormSubmit}>
               <Title text={exhibitDAml.title} className='align-header-text-sub-header' contentId='content1' />
               <Paragraph>
                 <p>The purpose of this <b>AML/KYC Background Information and Documentation</b>{exhibitDAml.purpose}</p>
               </Paragraph>
               <Paragraph text={exhibitDAml.capitalizedTerms} />
                {this.renderFullLegalNameInput()}
                {exhibitDAml.investorIdentification.map(this.renderExhibitDInput)}

                <Title text='A. General Questions' className='align-header-text-sub-header' contentId='content2' />
                <Paragraph text='All investors must carefully read and answer each of the following questions:' />
                {exhibitDAml.generalQuestions.map(this.renderGeneralQuestion)}
                 <Paragraph text='6. Please describe the source of the funds used by the undersigned in connection with the subscription hereunder:' />
                {this.renderExhibitDCheckboxes(exhibitDAml.sourceOfFunds, this.handlerExhibitDCheckbox)}
                {this.parseMultiSelectStateFromId('aml_source_of_funds_other') && this.renderExhibitDInput({ id: 'aml_general_source_of_funds_other', displayName: 'Other source of funds' })}

                <Title text='B. Identification of the Undersigned and Exemptions' className='align-header-text-sub-header' contentId='content3' />
                <Paragraph text={exhibitDAml.partBInstructions} />
                {exhibitDAml.partBCategories.map((item) => (
                  <div className='row' key={item[0]}>
                    {this.renderExhibitDCheckboxes([item], this.handlerPartBSelection)}
                    {this.parseMultiSelectStateFromId(item[0]) && item[2] && item[2].map((field, index) => this.renderExhibitDInput({ id: `${item[0]}_detail_${index + 1}`, displayName: field }))}
                  </div>
                ))}
                <Paragraph text='If none of the above categories applies, please continue to Part C. If any of the above categories applies, please skip to Part D.' />

                <fieldset disabled={partBExemptionSelected} className={partBExemptionSelected ? 'color-gray' : ''} style={{ border: 0, margin: 0, padding: 0 }}>
                   <Title text='C. Identification of Beneficial Owners' className='align-header-text-sub-header' contentId='content4' />
                   <Paragraph text={exhibitDAml.partCInstructions} />
                   {this.renderBeneficialOwnershipConfirm()}
                   {this.renderExhibitDCheckboxes(exhibitDAml.beneficialOwnership.slice(2), (event) => this.handlerBeneficialOwnershipExemption(event, exhibitDAml.beneficialOwnership.slice(2)))}
                  <Paragraph text={'If "Yes", please provide the following information for each 25% beneficial owner and provide all of the required documentation described in Part E below for a natural person. Note: If an entity of a type listed in Part B above owns, directly or indirectly, 25% or more of the equity interests of the undersigned, no natural person needs to be identified with respect to that entity\'s ownership interests.'} />
                  {this.parseMultiSelectStateFromId(exhibitDAml.beneficialOwnership[0].id) && Object.keys(exhibitDAml.owners).map((number) => (
                    <section className='row' key={`aml-owner-${number}`}>
                      <Title text={`Beneficial Owner ${number}`} className='align-header-text-sub-header' />
                      {this.renderExhibitDInput({ id: exhibitDAml.owners[number].name, displayName: 'Name' })}
                      {this.renderExhibitDInput({ id: exhibitDAml.owners[number].birthDate, displayName: 'Date of Birth' })}
                       {this.renderExhibitDInput({ id: exhibitDAml.owners[number].address, displayName: 'Address (residential or business street address):' })}
                       {this.renderExhibitDInput({ id: exhibitDAml.owners[number].usId, displayName: 'For U.S. Persons: Social Security Number' })}
                       {this.renderExhibitDInput({ id: exhibitDAml.owners[number].foreignId, displayName: 'For Foreign Persons: Social Security Number; Passport Number and Country of Issuance; or similar identification number', footnoteMarker: '31', helpText: 'In lieu of a passport number, foreign persons may provide an alien identification card number or number and country of issuance of any other government-issued document evidencing nationality or residence and bearing a photograph or similar safeguard.' })}
                    </section>
                  ))}
                   <Paragraph text='2. For one individual with significant responsibility for controlling, managing or directing the undersigned, please provide the following information. Such individual could be an executive officer or a senior manager (e.g., Chief Executive Officer, Chief Financial Officer, Chief Operating Officer, Managing Member, General Partner, President, Vice President, Treasurer) or another individual who regularly performs similar functions.' />
                   {exhibitDAml.controlPerson.map(this.renderExhibitDInput)}
                   <Paragraph text={"For each of the above-named persons in Questions (1) and (2), please also provide (as requested in Part E) a copy of the person's passport, driver's license, or other similar government-issued identification."} />
                   <Paragraph>
                     <p><i>Please note that the undersigned must notify the General Partner promptly of any change in the identity of the beneficial owners or control person required to be identified in this Part C.</i></p>
                   </Paragraph>
                   <Paragraph text='Please continue to Part D.' />
                </fieldset>

                <Title text='D. Nominees' className='align-header-text-sub-header' contentId='content5' />
                 {this.renderConfirm({ id: 'aml_nominee', displayName: '1. Is the undersigned acting as agent or nominee for another person or entity?' })}
                 {this.parseMultiSelectStateFromId('aml_nominee') && this.renderExhibitDInput({ id: 'aml_nominee_details', displayName: 'If "Yes", please list the underlying investors and/or beneficial owner(s):' })}
                 <Paragraph text='Please also provide:' />
                 <div style={{ paddingLeft: '32px' }}>
                   <Paragraph text='(a) information for the undersigned as requested in Part E (as applicable);' />
                   <Paragraph text={"(b) a representation or \"comfort\" letter regarding the undersigned's anti-money laundering, counter-terrorist financing and sanctions compliance program and procedures; and"} />
                   <Paragraph text='(c) any additional information that may be requested by the General Partner with respect to the underlying investors and/or beneficial owners.' />
                 </div>
                 <Paragraph>
                   <p><i>Please note that the undersigned must notify the General Partner promptly of any change in the identity of any underlying investor or beneficial owner required to be identified in this Part D.</i></p>
                 </Paragraph>

                 <Title text='E. Additional Required Documentation' className='align-header-text-sub-header' contentId='content6' />
                <Paragraph text={exhibitDAml.partEInstructions} />
                <Paragraph text='If supporting documentation is required, you will be provided with a secure Box link through which to submit the requested materials.' />
                <Paragraph text='Please check all that are applicable.' />
                {exhibitDAml.partEDocumentation.map((item) => (
                  <div className='row' key={item[0]}>
                    {this.renderExhibitDCheckboxes([item], this.handlerExhibitDCheckbox)}
                    <div style={{ paddingLeft: '74px' }}>
                      {item[2].map((requirement) => (
                        this.renderPartERequirement(item[0], requirement)
                      ))}
                    </div>
                  </div>
                ))}

                <div className='margin-bottom-spacing'>
                  <div className='next-btn' style={{ marginTop: '40px' }}>
                    <Button inputData={{ id: 'next-btn', displayName: 'Next', type: 'submit' }} />
                  </div>
                </div>
              </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null;
  }
}

const mapStateToProps = (state: Reducer) => ({
  questionnaireContext: state.questionnaireExperience,
});
const connector: Connector<{}, Props> = connect(mapStateToProps, (dispatch: Dispatch) => ({}));

export default connector(QuestionnaireForm);
